import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Euro,
  Calendar,
  DollarSign,
  Home,
  Users,
  Percent,
  Clock,
  BarChart3,
  LineChart as LineChartIcon,
  AreaChart as AreaChartIcon,
  XCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const KPIPanoramica = ({ bookings, filters }) => {
  const [stayChartType, setStayChartType] = useState('line'); // line, bar, area
  const [leadTimeChartType, setLeadTimeChartType] = useState('line');

  // Filter bookings (only active bookings by default)
  const filterBookings = (bookingsList, includeCancelled = false) => {
    return bookingsList.filter(booking => {
      // Skip cancelled bookings unless explicitly requested
      if (!includeCancelled && booking.cancelled) return false;
      if (includeCancelled && !booking.cancelled) return false;
      
      const bookingDate = new Date(booking.booking_date);
      const bookingYear = bookingDate.getFullYear();
      const bookingMonth = bookingDate.getMonth() + 1;
      const bookingQuarter = Math.ceil(bookingMonth / 3);

      if (bookingYear !== filters.year) return false;

      if (filters.period === 'month' && filters.month) {
        return bookingMonth === parseInt(filters.month);
      }
      
      if (filters.period === 'quarter' && filters.quarter) {
        return bookingQuarter === parseInt(filters.quarter);
      }

      if (filters.propertyId && booking.property_id !== filters.propertyId) {
        return false;
      }

      return true;
    });
  };

  const currentBookings = filterBookings(bookings);
  const previousBookings = filterBookings(bookings.map(b => ({
    ...b,
    booking_date: new Date(new Date(b.booking_date).setFullYear(filters.year - 1)).toISOString()
  })));

  // Calculate metrics
  const calculateMetrics = (bookingsList) => {
    const total = bookingsList.reduce((sum, b) => sum + (b.guest_total || 0), 0);
    const collected = bookingsList.filter(b => b.payment_status === 'collected').reduce((sum, b) => sum + (b.guest_total || 0), 0);
    const toCollect = bookingsList.filter(b => b.payment_status === 'to_collect').reduce((sum, b) => sum + (b.guest_total || 0), 0);
    const margin = bookingsList.reduce((sum, b) => sum + (b.margin || 0), 0);
    
    // Calculate average stay duration
    const stayDurations = bookingsList.map(b => {
      const checkIn = new Date(b.check_in);
      const checkOut = new Date(b.check_out);
      const diffTime = Math.abs(checkOut - checkIn);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    });
    const avgStay = stayDurations.length > 0 
      ? stayDurations.reduce((a, b) => a + b, 0) / stayDurations.length 
      : 0;

    // Calculate average booking lead time (days between booking date and check-in)
    const leadTimes = bookingsList.map(b => {
      const bookingDate = new Date(b.booking_date);
      const checkIn = new Date(b.check_in);
      const diffTime = Math.abs(checkIn - bookingDate);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    });
    const avgLeadTime = leadTimes.length > 0
      ? leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length
      : 0;

    return {
      totalBookings: bookingsList.length,
      totalRevenue: total,
      collected,
      toCollect,
      margin,
      averageBookingValue: bookingsList.length > 0 ? total / bookingsList.length : 0,
      avgStayDuration: avgStay,
      avgLeadTime: avgLeadTime
    };
  };

  const current = calculateMetrics(currentBookings);
  const previous = calculateMetrics(previousBookings);

  const calculateChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return ((curr - prev) / prev) * 100;
  };

  const changes = {
    totalBookings: calculateChange(current.totalBookings, previous.totalBookings),
    totalRevenue: calculateChange(current.totalRevenue, previous.totalRevenue),
    collected: calculateChange(current.collected, previous.collected),
    margin: calculateChange(current.margin, previous.margin),
    avgStay: calculateChange(current.avgStayDuration, previous.avgStayDuration),
    avgLeadTime: calculateChange(current.avgLeadTime, previous.avgLeadTime)
  };

  // Calculate cancelled bookings metrics
  const currentCancelled = filterBookings(bookings, true);
  const previousCancelled = filterBookings(bookings.map(b => ({
    ...b,
    booking_date: new Date(new Date(b.booking_date).setFullYear(filters.year - 1)).toISOString()
  })), true);

  const cancelledCurrent = calculateMetrics(currentCancelled);
  const cancelledPrevious = calculateMetrics(previousCancelled);

  const cancelledChanges = {
    totalBookings: calculateChange(cancelledCurrent.totalBookings, cancelledPrevious.totalBookings),
    totalRevenue: calculateChange(cancelledCurrent.totalRevenue, cancelledPrevious.totalRevenue)
  };

  // Calculate cancellation rate
  const totalCurrentBookings = current.totalBookings + cancelledCurrent.totalBookings;
  const cancellationRate = totalCurrentBookings > 0 
    ? (cancelledCurrent.totalBookings / totalCurrentBookings) * 100 
    : 0;

  // Prepare data for stay duration chart by month
  const prepareStayDurationData = () => {
    const monthlyData = {};
    
    currentBookings.forEach(booking => {
      const date = new Date(booking.booking_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { stays: [], month: monthKey };
      }
      
      const checkIn = new Date(booking.check_in);
      const checkOut = new Date(booking.check_out);
      const diffTime = Math.abs(checkOut - checkIn);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      monthlyData[monthKey].stays.push(days);
    });

    return Object.keys(monthlyData)
      .sort()
      .map(key => {
        const stays = monthlyData[key].stays;
        const avgStay = stays.reduce((a, b) => a + b, 0) / stays.length;
        const [year, month] = key.split('-');
        const monthName = new Date(year, parseInt(month) - 1).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
        
        return {
          month: monthName,
          soggiorno: parseFloat(avgStay.toFixed(1)),
          prenotazioni: stays.length
        };
      });
  };

  // Prepare data for lead time chart by month
  const prepareLeadTimeData = () => {
    const monthlyData = {};
    
    currentBookings.forEach(booking => {
      const date = new Date(booking.booking_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { leadTimes: [], month: monthKey };
      }
      
      const bookingDate = new Date(booking.booking_date);
      const checkIn = new Date(booking.check_in);
      const diffTime = Math.abs(checkIn - bookingDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      monthlyData[monthKey].leadTimes.push(days);
    });

    return Object.keys(monthlyData)
      .sort()
      .map(key => {
        const leadTimes = monthlyData[key].leadTimes;
        const avgLeadTime = leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length;
        const [year, month] = key.split('-');
        const monthName = new Date(year, parseInt(month) - 1).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
        
        return {
          month: monthName,
          anticipo: parseFloat(avgLeadTime.toFixed(1)),
          prenotazioni: leadTimes.length
        };
      });
  };

  // Prepare lead time distribution data
  const prepareLeadTimeDistribution = () => {
    const distribution = {
      '0-7 giorni': 0,
      '8-14 giorni': 0,
      '15-30 giorni': 0,
      '31-60 giorni': 0,
      '61-90 giorni': 0,
      '90+ giorni': 0
    };

    currentBookings.forEach(booking => {
      const bookingDate = new Date(booking.booking_date);
      const checkIn = new Date(booking.check_in);
      const diffTime = Math.abs(checkIn - bookingDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (days <= 7) distribution['0-7 giorni']++;
      else if (days <= 14) distribution['8-14 giorni']++;
      else if (days <= 30) distribution['15-30 giorni']++;
      else if (days <= 60) distribution['31-60 giorni']++;
      else if (days <= 90) distribution['61-90 giorni']++;
      else distribution['90+ giorni']++;
    });

    return Object.keys(distribution).map(key => ({
      range: key,
      prenotazioni: distribution[key],
      percentuale: currentBookings.length > 0 
        ? ((distribution[key] / currentBookings.length) * 100).toFixed(1)
        : 0
    }));
  };

  const stayDurationData = prepareStayDurationData();
  const leadTimeData = prepareLeadTimeData();
  const leadTimeDistribution = prepareLeadTimeDistribution();

  const COLORS = ['#1e3a8a', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe', '#e0e7ff'];

  const StatCard = ({ title, currentValue, change, icon: Icon, color, subtitle }) => {
    const TrendIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
    const trendColor = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
    
    return (
      <Card className="p-6 bg-gradient-to-br from-white to-gray-50 shadow-lg hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{currentValue}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            
            <div className="mt-3 flex items-center space-x-2">
              <TrendIcon className={`h-4 w-4 ${trendColor}`} />
              <span className={`text-sm font-semibold ${trendColor}`}>
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
              <span className="text-xs text-gray-500">vs {filters.year - 1}</span>
            </div>
          </div>
          <div className={`p-3 rounded-full bg-gradient-to-br ${color.replace('text-', 'from-')}-100 ${color.replace('text-', 'to-')}-200`}>
            <Icon className={`h-8 w-8 ${color}`} />
          </div>
        </div>
      </Card>
    );
  };

  const ChartTypeSelector = ({ currentType, onChange }) => {
    const types = [
      { value: 'line', icon: LineChartIcon, label: 'Linee' },
      { value: 'bar', icon: BarChart3, label: 'Barre' },
      { value: 'area', icon: AreaChartIcon, label: 'Area' }
    ];

    return (
      <div className="flex space-x-2">
        {types.map(type => {
          const Icon = type.icon;
          return (
            <Button
              key={type.value}
              variant={currentType === type.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChange(type.value)}
              className="flex items-center space-x-1"
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{type.label}</span>
            </Button>
          );
        })}
      </div>
    );
  };

  const renderChart = (data, dataKey, chartType, color) => {
    const chartProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    const commonProps = {
      type: "monotone",
      dataKey: dataKey,
      stroke: color,
      fill: color,
      strokeWidth: 2
    };

    return (
      <ResponsiveContainer width="100%" height={300}>
        {chartType === 'line' ? (
          <LineChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
            <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Legend />
            <Line {...commonProps} name={dataKey === 'soggiorno' ? 'Soggiorno (giorni)' : 'Anticipo (giorni)'} />
          </LineChart>
        ) : chartType === 'bar' ? (
          <BarChart {...chartProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
            <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Legend />
            <Bar {...commonProps} name={dataKey === 'soggiorno' ? 'Soggiorno (giorni)' : 'Anticipo (giorni)'} radius={[8, 8, 0, 0]} />
          </BarChart>
        ) : (
          <AreaChart {...chartProps}>
            <defs>
              <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
            <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Legend />
            <Area 
              {...commonProps} 
              name={dataKey === 'soggiorno' ? 'Soggiorno (giorni)' : 'Anticipo (giorni)'} 
              fill={`url(#gradient-${dataKey})`}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📊 Panoramica Generale</h2>
        <p className="text-gray-600">Vista completa delle metriche chiave del periodo selezionato</p>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Prenotazioni Totali"
          currentValue={current.totalBookings}
          change={changes.totalBookings}
          icon={Calendar}
          color="text-blue-900"
        />
        <StatCard
          title="Valore Totale"
          currentValue={`€${current.totalRevenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          change={changes.totalRevenue}
          icon={Euro}
          color="text-blue-600"
          subtitle={`Media: €${current.averageBookingValue.toFixed(2)}`}
        />
        <StatCard
          title="Incassato"
          currentValue={`€${current.collected.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          change={changes.collected}
          icon={DollarSign}
          color="text-green-600"
        />
        <StatCard
          title="Margine HW"
          currentValue={`€${current.margin.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          change={changes.margin}
          icon={TrendingUp}
          color="text-emerald-600"
        />
      </div>

      {/* New KPIs: Duration & Lead Time */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">⏱️ Metriche Temporali</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Soggiorno Medio"
            currentValue={`${current.avgStayDuration.toFixed(1)} giorni`}
            change={changes.avgStay}
            icon={Clock}
            color="text-purple-600"
            subtitle="Durata media check-in → check-out"
          />
          <StatCard
            title="Anticipo Medio Prenotazione"
            currentValue={`${current.avgLeadTime.toFixed(0)} giorni`}
            change={changes.avgLeadTime}
            icon={Calendar}
            color="text-cyan-600"
            subtitle="Giorni tra prenotazione e arrivo"
          />
        </div>
      </div>

      {/* Stay Duration Trend Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">📈 Trend Soggiorno Medio</h3>
            <p className="text-sm text-gray-600">Evoluzione della durata media dei soggiorni nel tempo</p>
          </div>
          <ChartTypeSelector currentType={stayChartType} onChange={setStayChartType} />
        </div>
        {stayDurationData.length > 0 ? (
          renderChart(stayDurationData, 'soggiorno', stayChartType, '#9333ea')
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Nessun dato disponibile per il periodo selezionato
          </div>
        )}
      </Card>

      {/* Lead Time Trend Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">📈 Trend Anticipo Prenotazione</h3>
            <p className="text-sm text-gray-600">Evoluzione del tempo medio tra prenotazione e arrivo</p>
          </div>
          <ChartTypeSelector currentType={leadTimeChartType} onChange={setLeadTimeChartType} />
        </div>
        {leadTimeData.length > 0 ? (
          renderChart(leadTimeData, 'anticipo', leadTimeChartType, '#0891b2')
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Nessun dato disponibile per il periodo selezionato
          </div>
        )}
      </Card>

      {/* Lead Time Distribution */}
      <Card className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-900">📊 Distribuzione Anticipo Prenotazione</h3>
          <p className="text-sm text-gray-600">Quanto tempo in anticipo prenotano gli ospiti</p>
        </div>
        {leadTimeDistribution.some(d => d.prenotazioni > 0) ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={leadTimeDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="range" 
                  stroke="#6b7280" 
                  style={{ fontSize: '11px' }} 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [value, 'Prenotazioni']}
                />
                <Bar dataKey="prenotazioni" fill="#0891b2" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Pie Chart */}
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={leadTimeDistribution.filter(d => d.prenotazioni > 0)}
                  dataKey="prenotazioni"
                  nameKey="range"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ range, percentuale }) => `${range}: ${percentuale}%`}
                  labelLine={false}
                >
                  {leadTimeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name, props) => [
                    `${value} prenotazioni (${props.payload.percentuale}%)`,
                    props.payload.range
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Nessun dato disponibile per il periodo selezionato
          </div>
        )}
      </Card>

      {/* Summary Table */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Riepilogo Completo</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Metrica</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">{filters.year}</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">{filters.year - 1}</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Variazione</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Prenotazioni</td>
                <td className="text-right py-3 px-4 font-semibold">{current.totalBookings}</td>
                <td className="text-right py-3 px-4 text-gray-600">{previous.totalBookings}</td>
                <td className="text-right py-3 px-4">
                  <span className={`font-semibold ${changes.totalBookings > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {changes.totalBookings > 0 ? '+' : ''}{changes.totalBookings.toFixed(1)}%
                  </span>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Ricavi Totali</td>
                <td className="text-right py-3 px-4 font-semibold">€{current.totalRevenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}</td>
                <td className="text-right py-3 px-4 text-gray-600">€{previous.totalRevenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}</td>
                <td className="text-right py-3 px-4">
                  <span className={`font-semibold ${changes.totalRevenue > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {changes.totalRevenue > 0 ? '+' : ''}{changes.totalRevenue.toFixed(1)}%
                  </span>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Soggiorno Medio</td>
                <td className="text-right py-3 px-4 font-semibold">{current.avgStayDuration.toFixed(1)} gg</td>
                <td className="text-right py-3 px-4 text-gray-600">{previous.avgStayDuration.toFixed(1)} gg</td>
                <td className="text-right py-3 px-4">
                  <span className={`font-semibold ${changes.avgStay > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {changes.avgStay > 0 ? '+' : ''}{changes.avgStay.toFixed(1)}%
                  </span>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Anticipo Prenotazione</td>
                <td className="text-right py-3 px-4 font-semibold">{current.avgLeadTime.toFixed(0)} gg</td>
                <td className="text-right py-3 px-4 text-gray-600">{previous.avgLeadTime.toFixed(0)} gg</td>
                <td className="text-right py-3 px-4">
                  <span className={`font-semibold ${changes.avgLeadTime > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {changes.avgLeadTime > 0 ? '+' : ''}{changes.avgLeadTime.toFixed(1)}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Cancelled Bookings Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">🚫 Prenotazioni Annullate</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Prenotazioni Annullate"
            currentValue={cancelledCurrent.totalBookings}
            change={cancelledChanges.totalBookings}
            icon={XCircle}
            color="text-red-600"
            subtitle={`Tasso: ${cancellationRate.toFixed(1)}%`}
          />
          <StatCard
            title="Valore Annullato"
            currentValue={`€${cancelledCurrent.totalRevenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={cancelledChanges.totalRevenue}
            icon={Euro}
            color="text-orange-600"
            subtitle={`Media: €${(cancelledCurrent.totalBookings > 0 ? cancelledCurrent.totalRevenue / cancelledCurrent.totalBookings : 0).toFixed(2)}`}
          />
          <StatCard
            title="Margine Perso"
            currentValue={`€${cancelledCurrent.margin.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={calculateChange(cancelledCurrent.margin, cancelledPrevious.margin)}
            icon={TrendingDown}
            color="text-red-700"
            subtitle="Margine che sarebbe stato generato"
          />
        </div>

        {/* Cancellation insights */}
        <Card className="p-6 mt-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">📈 Analisi Annullamenti</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-gray-700 mb-3">Confronto Anno su Anno</h5>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2">Metrica</th>
                    <th className="text-right py-2">{filters.year}</th>
                    <th className="text-right py-2">{filters.year - 1}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2">Prenotazioni Annullate</td>
                    <td className="text-right font-semibold">{cancelledCurrent.totalBookings}</td>
                    <td className="text-right text-gray-600">{cancelledPrevious.totalBookings}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2">Tasso di Cancellazione</td>
                    <td className="text-right font-semibold">{cancellationRate.toFixed(1)}%</td>
                    <td className="text-right text-gray-600">
                      {((cancelledPrevious.totalBookings / (previous.totalBookings + cancelledPrevious.totalBookings)) * 100).toFixed(1)}%
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2">Valore Annullato</td>
                    <td className="text-right font-semibold">€{cancelledCurrent.totalRevenue.toFixed(2)}</td>
                    <td className="text-right text-gray-600">€{cancelledPrevious.totalRevenue.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h5 className="font-semibold text-gray-700 mb-3">Impatto Finanziario</h5>
              <div className="space-y-3">
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Ricavi Potenziali Persi</div>
                  <div className="text-2xl font-bold text-red-600">
                    €{cancelledCurrent.totalRevenue.toFixed(2)}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Margine Potenziale Perso</div>
                  <div className="text-2xl font-bold text-orange-600">
                    €{cancelledCurrent.margin.toFixed(2)}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Prenotazioni Totali (Attive + Annullate)</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {totalCurrentBookings}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default KPIPanoramica;